/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Homepage route: JSON-LD + composition only.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import { trpc } from '@/lib/trpc';
import { createPageMeta, PAGE_KEYWORDS } from '@/lib/meta';
import { socialSameAsUrls } from '@/lib/socialLinks';
import { HomeHero } from '@/components/home/HomeHero';
import { HomeCurrentFocus } from '@/components/home/HomeCurrentFocus';
import { HomeFeaturedWriting } from '@/components/home/HomeFeaturedWriting';
import { HomeWork } from '@/components/home/HomeWork';
import { latestManifestPosts, parseHomeSearch } from '@/lib/homeFinder';

export const Route = createFileRoute('/')({
  component: HomePage,
  validateSearch: parseHomeSearch,
  loader: async ({ context }) => {
    try {
      await Promise.all([
        context.queryClient.ensureQueryData(trpc.blog.latest.queryOptions({ limit: 8 })),
        context.queryClient.ensureQueryData(trpc.spotify.libraryTeaser.queryOptions()),
      ]);
    } catch {
      // DB unavailable — render empty teasers rather than blocking SSR
    }
  },
  head: () => {
    const base = createPageMeta({
      title: 'Christian Battaglia — Product Engineer, Builder, Creator',
      description:
        'Product engineer building open source tools and exploring music technology. Writing about infrastructure, frameworks, and creative technical solutions.',
      path: '/',
      image: '/assets-cdbattaglia/media/images/og-homepage.png',
      imageAlt: 'Christian Battaglia — Product Engineer, Builder, Creator',
      keywords: PAGE_KEYWORDS.home,
    });
    return {
      ...base,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ProfilePage',
            mainEntity: {
              '@type': 'Person',
              '@id': 'https://cdbattaglia.com/#person',
              name: 'Christian Battaglia',
              alternateName: 'Chris Battaglia',
              jobTitle: 'Chief Technology Officer',
              worksFor: {
                '@type': 'Organization',
                name: 'Slabstack',
                url: 'https://slabstack.com',
              },
              alumniOf: {
                '@type': 'CollegeOrUniversity',
                name: 'Georgia Institute of Technology',
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Brooklyn',
                addressRegion: 'NY',
                addressCountry: 'US',
              },
              url: 'https://cdbattaglia.com',
              image: 'https://cdbattaglia.com/assets-cdbattaglia/media/images/og-homepage.png',
              sameAs: socialSameAsUrls(),
              knowsLanguage: [
                { '@type': 'Language', name: 'English' },
                { '@type': 'Language', name: 'Spanish' },
              ],
              knowsAbout: [
                'JavaScript', 'TypeScript', 'Python', 'Ruby', 'Java', 'Rust',
                'React', 'Vue', 'Angular', 'TanStack Start',
                'Node.js', 'FastAPI', 'Ruby on Rails',
                'AWS', 'Azure', 'Docker', 'Kubernetes',
                'PostgreSQL', 'MongoDB', 'Redis',
                'Music Technology', 'Audio Analysis', 'Spotify API',
                'Infrastructure as Code', 'DevOps',
              ],
            },
          }),
        },
      ],
    };
  },
});

function HomePage() {
  const { data } = useQuery(trpc.blog.latest.queryOptions({ limit: 8 }));

  return (
    <Box className="min-h-screen">
      <HomeHero />
      <HomeCurrentFocus />
      <HomeFeaturedWriting posts={data?.posts?.length ? data.posts : latestManifestPosts()} />
      <HomeWork />
    </Box>
  );
}
